/**
 * PPC Ads Landing Pages — static config map.
 *
 * Source key: ppc_ads (matches backend/services/crm_bridge_service.py + crm_sync_service.py)
 * Routes: /lp/<slug>  +  /lp/<slug>/thank-you
 * Docs:   /app/docs/PPC_ADS_LP_INTERNAL_SPEC.md
 *
 * To add a 7th LP, add a new entry here AND add the slug to PPC_ADS_ALLOWED_SLUGS
 * in backend/server.py.
 *
 * IMPORTANT — copy guidelines:
 *   - Phone and email come from siteConfig (single source of truth, do not hardcode)
 *   - DO NOT use the word "free" anywhere — KGOB does not offer free consultations
 *   - Use "Request", "Schedule", "Talk to", "Plan", etc. as CTAs
 */

import { siteConfig } from '@/lib/siteConfig';

// Phone and email — pulled from siteConfig (canonical). Phone dial uses digits-only form for tel: links.
const PHONE_DISPLAY = siteConfig.phoneDisplay;          // "1-844-599-3355"
const PHONE_DIAL = siteConfig.phone.replace(/\D/g, '').replace(/^/, '+');  // "+18445993355"
const EMAIL = siteConfig.email;                          // "connect@kgob.com"

// Allowed service enum (must match PPC_ADS_ALLOWED_SERVICES in backend/server.py)
export const ALL_SERVICES = [
  { id: "tax_planning",          label: "Tax planning & prep" },
  { id: "business_tax",          label: "Business tax planning" },
  { id: "accounting_bookkeeping",label: "Accounting & bookkeeping" },
  { id: "small_business_cpa",    label: "Small business CPA" },
  { id: "irs_tax_relief",        label: "IRS tax relief" },
  { id: "irs_notice_help",       label: "IRS notice help" },
];

export const LP_CONFIG = {
  "tax-planning": {
    slug: "tax-planning",
    h1: "Tax Planning & Prep That Actually Saves You Money",
    subhead: "Strategic tax planning for individuals and businesses across the Carolinas. We find every legitimate deduction — and plan ahead so April is never a surprise.",
    audience: "both",
    services_pre_checked: ["tax_planning"],
    features: [
      "Quarterly tax planning to avoid year-end surprises",
      "Federal + state filings handled end-to-end by a licensed CPA",
      "S-Corp election analysis for small business owners",
      "IRS-compliant, audit-defensible documentation",
    ],
    cta: "Request a Tax Planning Consult",
    thank_you_headline: "Got it — we'll be in touch shortly.",
    thank_you_subhead: "A KGOB tax advisor will reach out within 1 business day to schedule a planning conversation and walk you through next steps.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },

  "business-tax": {
    slug: "business-tax",
    h1: "Business Tax Planning Built Around Your Growth",
    subhead: "From S-Corp elections to quarterly estimates — KGOB partners with business owners across NC and SC to lower the tax bill and protect cash flow.",
    audience: "business",
    services_pre_checked: ["business_tax"],
    features: [
      "Entity-structure review (LLC, S-Corp, C-Corp)",
      "Quarterly estimated-tax planning to avoid penalties",
      "Multi-state nexus and filings handled cleanly",
      "Year-end strategy to capture every legitimate deduction",
    ],
    cta: "Request a Business Tax Consult",
    thank_you_headline: "Received — your business tax review is queued.",
    thank_you_subhead: "We'll review your information and a KGOB CPA will reach out within 1 business day to schedule a planning conversation.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },

  "accounting-bookkeeping": {
    slug: "accounting-bookkeeping",
    h1: "Accurate Books. Clean Reports. Sleep Better.",
    subhead: "Monthly bookkeeping, financial statements, and payroll integration — done by a real CPA team, not a back-office overseas. We make your numbers tell the truth.",
    audience: "business",
    services_pre_checked: ["accounting_bookkeeping"],
    features: [
      "Monthly bookkeeping in QuickBooks, Xero, or your platform",
      "Clean financial statements ready for lenders and investors",
      "Payroll + sales tax integration handled by our team",
      "Year-end books always tax-ready — no scramble in March",
    ],
    cta: "Request a Bookkeeping Consult",
    thank_you_headline: "Thanks — let's get those books in order.",
    thank_you_subhead: "A KGOB advisor will reach out within 1 business day to scope a monthly bookkeeping plan that fits your business.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },

  "small-business-cpa": {
    slug: "small-business-cpa",
    h1: "The Small Business CPA That Actually Picks Up the Phone",
    subhead: "Full-service CPA support for small businesses across the Carolinas. Tax, bookkeeping, advisory — one team, one accountable point of contact.",
    audience: "business",
    services_pre_checked: ["small_business_cpa"],
    features: [
      "One CPA team across tax, books, and advisory — no hand-offs",
      "Same-day response policy for client calls and emails",
      "Quarterly business reviews to plan ahead, not just react",
      "Transparent monthly pricing — no surprise invoices",
    ],
    cta: "Talk to a KGOB CPA",
    thank_you_headline: "Welcome — let's set up your intro call.",
    thank_you_subhead: "A KGOB CPA will reach out within 1 business day to learn about your business and walk you through how we work.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },

  "irs-tax-relief": {
    slug: "irs-tax-relief",
    h1: "IRS Tax Relief Backed by a Real CPA Firm",
    subhead: "Back taxes, wage garnishment, bank levies, unfiled returns — we resolve IRS problems for individuals and businesses with a confidential case review.",
    audience: "both",
    services_pre_checked: ["irs_tax_relief"],
    features: [
      "Offer in Compromise analysis (real eligibility, not marketing)",
      "Installment Agreements that fit your cash flow",
      "Penalty Abatement for first-time and reasonable-cause cases",
      "Audit representation by a licensed CPA",
    ],
    cta: "Request an IRS Case Review",
    thank_you_headline: "Received — your IRS case is in good hands.",
    thank_you_subhead: "A KGOB tax-resolution specialist will reach out within 1 business day. All communication is confidential and protected.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },

  "irs-notice-help": {
    slug: "irs-notice-help",
    h1: "Got an IRS Notice? Don't Panic — We Handle These Daily.",
    subhead: "CP2000, LT11, CP504, Notice of Deficiency — KGOB responds for you. We translate the IRS letter, build the response, and protect what's yours.",
    audience: "both",
    services_pre_checked: ["irs_notice_help"],
    features: [
      "CP2000 (under-reported income) responses with full documentation",
      "LT11 / CP504 (Final Notice of Intent to Levy) urgent response",
      "Notice of Deficiency (90-day letter) tax court protective filings",
      "Deadline tracking so nothing falls through the cracks",
    ],
    cta: "Get Help With My IRS Notice",
    thank_you_headline: "Received — we're on it.",
    thank_you_subhead: "A KGOB specialist will respond within 1 business day with next steps. You can also reply to our confirmation email with a photo of the notice.",
    phone_display: PHONE_DISPLAY,
    phone_dial: PHONE_DIAL,
    email: EMAIL,
  },
};

export const LP_SLUGS = Object.keys(LP_CONFIG);

export function getLpConfig(slug) {
  return LP_CONFIG[slug] || null;
}
